/*!custom-truncate-description.js

  Script di truncamento e sanitizzazione delle descrizioni prodotto.
  - Trunca il contenuto a N righe misurate realmente (in base a font e larghezza), con pulsante "Visualizza tutto / Mostra meno".
  - Sanifica l'HTML (rimuove script, stili inline, attributi non sicuri, href/src non sicuri) e normalizza <br>, spazi e blocchi vuoti.
  - Osserva mutazioni DOM e resize per riprocessare dinamicamente i nodi target.

  Funzioni esposte:
  - window.truncateDescriptionProcessAll(root): processa tutte le descrizioni che matchano i selettori, partendo da root (o document).
  - window.__TRUNCATE_forceResanitize(root): forza la risanificazione dell'HTML nei nodi target, invalida cache selettivamente e riprocessa.
*/

(() => {
  'use strict';

  // Configurazione di base: durate animazioni, righe predefinite, selettori e limiti cache
  const TRUNCATE_CONFIG = {
    OPEN_DURATION_MS: 0,
    CLOSE_DURATION_MS: 0,
    LINES_DEFAULT: 1,
    SPACER_HEIGHT: '20px',
    SELECTORS: [
      '.js-product-description',
      '.mb10.product-single__description.rte',
      '.product-single__description.rte'
    ],
    STYLE_ID: 'truncate-divs-in-place',
    SANITIZE_CACHE_LIMIT: 500,
    useRequestIdleCallback: true,
    telemetryCallback: null
  };

  const {
    OPEN_DURATION_MS,
    CLOSE_DURATION_MS,
    LINES_DEFAULT,
    SPACER_HEIGHT,
    SELECTORS,
    STYLE_ID
  } = TRUNCATE_CONFIG;

  // Selettore aggregato per query efficiente
  const SEL = SELECTORS.join(',');

  // Traduzioni per etichette pulsante
  const TRANSLATIONS = {
    it: { show: 'Mostra altro', hide: 'Mostra meno' },
    en: { show: 'Show more', hide: 'Show less' },
    fr: { show: 'Afficher plus', hide: 'Afficher moins' },
    de: { show: 'Mehr anzeigen', hide: 'Weniger anzeigen' },
    es: { show: 'Mostrar más', hide: 'Mostrar menos' }
  };

  // Regex per normalizzazione line-break e testo
  const BR_REGEX = /<br\s*\/?>/gi;
  const MULTI_SPACE_REGEX = /\s{2,}/g;
  const MULTI_NEWLINE_REGEX = /\n{2,}/g;
  const BR_TOKEN = '___BR_TOKEN___';
  const MULTI_BR_TOKEN_REGEX = /(___BR_TOKEN___){2,}/g;

  // Telemetria errori (opzionale)
  function trError(...args) {
    try {
      if (typeof TRUNCATE_CONFIG.telemetryCallback === 'function') {
        TRUNCATE_CONFIG.telemetryCallback('error', { args: args.map(a => String(a)) });
      }
    } catch (e) {}
    try {
      if (console && typeof console.error === 'function') console.error('[truncate][ERROR]', ...args);
    } catch (e) {}
  }

  // Iniezione stile iniziale: nasconde i target finché non processati, definisce classi testo/spacer e stile pulsante
  if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
    try {
      const hideSelectors = SELECTORS.map(s => `${s}:not([data-truncate-processed="true"])`).join(', ');
      const css =
        hideSelectors + ' { visibility: hidden !important; }\n' +
        `.${STYLE_ID}-text { overflow: hidden; margin: 0; padding: 0; }\n` +
        '.vVisualizzaTutto{background:transparent;border:none;font-weight:500;cursor:pointer;padding:0;color:inherit;text-decoration:none;position:relative;line-height:1;display:inline-block;padding-bottom:2px;margin:0}\n' +
        '.vVisualizzaTutto::after{content:"";position:absolute;left:0;right:0;height:1px;background:currentColor;bottom:-2px;pointer-events:none}\n' +
        `.${STYLE_ID}-spacer{display:block;height:${SPACER_HEIGHT};min-height:${SPACER_HEIGHT};margin:0;padding:0}\n`;
      const styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      styleEl.textContent = css;
      (document.head || document.getElementsByTagName('head')[0]).appendChild(styleEl);
    } catch (e) {
      trError('inject css failed', e);
    }
  }

  // Locale caching e rilevamento (html[lang], Shopify.locale, navigator.language)
  let _cachedLocale = null;
  const detectLocale = () => {
    if (_cachedLocale) return _cachedLocale;
    try {
      const htmlLang = (document.documentElement.lang || document.documentElement.getAttribute('lang') || '').slice(0, 2).toLowerCase();
      if (htmlLang) return (_cachedLocale = htmlLang);
      if (window && window.Shopify && window.Shopify.locale) return (_cachedLocale = String(window.Shopify.locale).slice(0, 2).toLowerCase());
      return (_cachedLocale = (navigator.language || 'it').slice(0, 2).toLowerCase());
    } catch (e) {
      trError('detectLocale', e);
      return (_cachedLocale = 'it');
    }
  };

  // Helper traduzioni
  const t = (key, locale) => (TRANSLATIONS[locale] || TRANSLATIONS.it)[key];

  // LRU semplice per cache di sanitizzazione
  class LRUCache {
    constructor(limit = TRUNCATE_CONFIG.SANITIZE_CACHE_LIMIT) {
      this.limit = limit;
      this.map = new Map();
    }
    get(key) {
      if (!this.map.has(key)) return undefined;
      const val = this.map.get(key);
      this.map.delete(key);
      this.map.set(key, val);
      return val;
    }
    set(key, value) {
      if (this.map.has(key)) this.map.delete(key);
      else if (this.map.size >= this.limit) this.map.delete(this.map.keys().next().value);
      this.map.set(key, value);
    }
    clear() { this.map.clear(); }
  }

  const _sanitizeMeasureCache = new LRUCache(TRUNCATE_CONFIG.SANITIZE_CACHE_LIMIT);
  const _sanitizeDisplayCache = new LRUCache(TRUNCATE_CONFIG.SANITIZE_CACHE_LIMIT);

  // Schemi sicuri per href/src
  const _ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];

  // Validazione href/src: consente data:image base64 per <img>, blocca data: per altri, consente path relativi e ancore
  function isHrefSafe(v, tagName) {
    if (!v) return false;
    const s = String(v).trim();
    const lower = s.toLowerCase();
    try {
      if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(s)) {
        if (lower.startsWith('data:')) {
          if (tagName === 'IMG' && /^data:image\/[a-z0-9.+-]+;base64,/i.test(s)) return true;
          return false;
        }
        const scheme = s.split(':', 1)[0].toLowerCase() + ':';
        return _ALLOWED_SCHEMES.includes(scheme);
      }
      if (s.startsWith('/') || s.startsWith('./') || s.startsWith('../') || s.startsWith('#')) return true;
      return false;
    } catch (e) {
      trError('isHrefSafe', e);
      return false;
    }
  }

  // Parser condiviso per ridurre allocazioni
  const _sharedDOMParser = (typeof DOMParser !== 'undefined') ? new DOMParser() : null;

  // Sanitizzazione HTML: rimuove tag pericolosi, attributi non consentiti e normalizza <br>
  function sanitizeHTMLStrict(inputHtml) {
    if (!inputHtml) return '';
    const parser = _sharedDOMParser || new DOMParser();
    const doc = parser.parseFromString(String(inputHtml), 'text/html');
    if (!doc || !doc.body) return '';
    try {
      ['script', 'style', 'iframe', 'object', 'embed'].forEach(tag => {
        const nodes = doc.body.querySelectorAll(tag);
        for (let i = nodes.length - 1; i >= 0; i--) {
          const n = nodes[i];
          if (n && n.parentNode) n.parentNode.removeChild(n);
        }
      });
      const ALLOWED_ATTRS = { A: ['href', 'title', 'target', 'rel'], IMG: ['src', 'alt', 'title', 'width', 'height'] };
      const all = Array.from(doc.body.querySelectorAll('*'));
      all.forEach(node => {
        const tag = (node.tagName || '').toUpperCase();
        const attrs = Array.from(node.attributes || []);
        attrs.forEach(a => {
          const name = a.name;
          const lname = name.toLowerCase();
          const val = a.value;
          if (lname.startsWith('on') || lname === 'style') { node.removeAttribute(name); return; }
          const allowedForTag = ALLOWED_ATTRS[tag] || [];
          if (!allowedForTag.map(x => x.toLowerCase()).includes(lname)) { node.removeAttribute(name); return; }
          if ((lname === 'href' || lname === 'src') && !isHrefSafe(val, tag)) { node.removeAttribute(name); return; }
        });
        if (tag === 'A') {
          const target = node.getAttribute('target');
          if (target && target.toLowerCase() === '_blank') {
            const rel = (node.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
            if (!rel.includes('noopener')) rel.push('noopener');
            if (!rel.includes('noreferrer')) rel.push('noreferrer');
            node.setAttribute('rel', rel.join(' '));
          }
        }
      });
      let html = doc.body.innerHTML;
      html = html.replace(BR_REGEX, BR_TOKEN);
      html = html.replace(MULTI_BR_TOKEN_REGEX, BR_TOKEN);
      return html.split(BR_TOKEN).join('<br>');
    } catch (e) {
      trError('sanitizeHTMLStrict', e);
      return '';
    }
  }

  // Normalizza testo, rimuove nodi vuoti e comprime <br> multipli
  function collapseBrsAndNormalizeTextIn(el) {
    try {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
      const toRemove = [];
      while (walker.nextNode()) {
        const tn = walker.currentNode;
        if (!tn.nodeValue) { toRemove.push(tn); continue; }

        let normalized = tn.nodeValue.replace(/\s+/g, ' ');

        if (normalized === ' ') {
          const prev = tn.previousSibling;
          const next = tn.nextSibling;

          const isInlineElement = node => {
            if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
            const inlineTags = ['A','SPAN','STRONG','B','EM','I','U','IMG','CODE','S','SUB','SUP'];
            return inlineTags.includes((node.tagName || '').toUpperCase()) || window.getComputedStyle(node).display === 'inline';
          };

          if ( (prev && (prev.nodeType === Node.TEXT_NODE || isInlineElement(prev))) &&
              (next && (next.nodeType === Node.TEXT_NODE || isInlineElement(next))) ) {
            tn.nodeValue = ' ';
          } else {
            toRemove.push(tn);
          }
          continue;
        }
        tn.nodeValue = normalized;
      }

      for (let i = 0; i < toRemove.length; i++) {
        const n = toRemove[i];
        if (n.parentNode) n.parentNode.removeChild(n);
      }

      let html = el.innerHTML;
      html = html.replace(BR_REGEX, BR_TOKEN);
      html = html.replace(MULTI_BR_TOKEN_REGEX, BR_TOKEN);
      el.innerHTML = html.split(BR_TOKEN).join('<br>');
    } catch (e) {
      trError('collapseBrsAndNormalizeTextIn', e);
    }
  }

  // Rimozione ricorsiva di blocchi vuoti non significativi
  function removeEmptyBlocksRecursively(root) {
    try {
      const blockTags = ['SECTION', 'ARTICLE', 'P', 'HEADER', 'FOOTER', 'ASIDE', 'NAV', 'UL', 'OL', 'LI', 'TABLE', 'TR', 'TD', 'TH', 'FIGURE', 'DIV'];
      const normalizedText = node => (node.textContent || '').replace(/[\s\u00A0]/g, '');
      function isEffectivelyEmpty(node) {
        if (node.querySelector && node.querySelector('img,video,iframe,svg')) return false;
        if (normalizedText(node).length > 0) return false;
        const children = Array.from(node.childNodes || []);
        if (children.length === 0) return true;
        for (let i = 0; i < children.length; i++) {
          const c = children[i];
          if (c.nodeType === Node.TEXT_NODE) {
            if (c.nodeValue && c.nodeValue.replace(/[\s\u00A0]/g, '').length > 0) return false;
            continue;
          }
          if (c.nodeType === Node.ELEMENT_NODE) {
            const tag = (c.tagName || '').toUpperCase();
            if (tag === 'BR') continue;
            if (c.querySelector && c.querySelector('img,video,iframe,svg')) return false;
            if (normalizedText(c).length > 0) return false;
            if (!isEffectivelyEmpty(c)) return false;
          }
        }
        return true;
      }
      const nodes = Array.from(root.querySelectorAll(blockTags.join(',')));
      for (let i = nodes.length - 1; i >= 0; i--) {
        const el = nodes[i];
        if (isEffectivelyEmpty(el) && el.parentNode) el.parentNode.removeChild(el);
      }
    } catch (e) {
      trError('removeEmptyBlocksRecursively', e);
    }
  }

  // Limite tempo per sanitizzazione (evita blocchi lunghi)
  const SANITIZE_TIME_LIMIT_MS = 200;

  // Esecuzione con budget di tempo e fallback
  function runWithTimeBudget(fn, args, limitMs, fallback) {
    const start = (performance && performance.now) ? performance.now() : Date.now();
    try {
      const res = fn.apply(null, args);
      const dur = ((performance && performance.now) ? performance.now() : Date.now()) - start;
      try {
        if (typeof TRUNCATE_CONFIG.telemetryCallback === 'function') {
          TRUNCATE_CONFIG.telemetryCallback('sanitize_duration', { duration: dur });
        }
      } catch (e) {}
      if (dur > limitMs) return fallback;
      return res;
    } catch (e) {
      trError('runWithTimeBudget error', e);
      return fallback;
    }
  }

  // Pipeline sanitizzazione per misura (senza rimozione blocchi)
  function sanitizeForMeasure(html) {
    if (!html) return '';
    const cached = _sanitizeMeasureCache.get(html);
    if (cached !== undefined) return cached;
    const sanitized = sanitizeHTMLStrict(html);
    const localDiv = document.createElement('div');
    localDiv.innerHTML = sanitized;
    collapseBrsAndNormalizeTextIn(localDiv);
    const out = localDiv.innerHTML;
    _sanitizeMeasureCache.set(html, out);
    return out;
  }

  // Pipeline sanitizzazione per display (con rimozione blocchi vuoti)
  function sanitizeForDisplay(html) {
    if (!html) return '';
    const cached = _sanitizeDisplayCache.get(html);
    if (cached !== undefined) return cached;
    const sanitized = sanitizeHTMLStrict(html);
    const localDiv = document.createElement('div');
    localDiv.innerHTML = sanitized;
    collapseBrsAndNormalizeTextIn(localDiv);
    removeEmptyBlocksRecursively(localDiv);
    const result = localDiv.innerHTML;
    _sanitizeDisplayCache.set(html, result);
    return result;
  }

  // Versioni con budget di tempo
  function sanitizeForMeasureWithBudget(html) { return runWithTimeBudget(sanitizeForMeasure, [html], SANITIZE_TIME_LIMIT_MS, ''); }
  function sanitizeForDisplayWithBudget(html) { return runWithTimeBudget(sanitizeForDisplay, [html], SANITIZE_TIME_LIMIT_MS, ''); }

  // Elemento invisibile per misurare linee/altezza reale
  let _measureEl = null;
  function ensureMeasureEl() {
    try {
      if (_measureEl && _measureEl.isConnected) return _measureEl;
      if (!_measureEl) {
        _measureEl = document.createElement('div');
        Object.assign(_measureEl.style, {
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          left: '-9999px',
          top: '0',
          whiteSpace: 'normal',
          margin: '0',
          padding: '0',
          overflow: 'visible'
        });
        _measureEl.setAttribute('aria-hidden', 'true');
      }
      if (document.body && !_measureEl.isConnected) document.body.appendChild(_measureEl);
      return _measureEl;
    } catch (e) {
      trError('ensureMeasureEl', e);
      return _measureEl;
    }
  }

  // Cache per computedStyle (evita chiamate ripetute)
  let _computedStyleCache = new WeakMap();
  function getCachedComputedStyle(el) {
    if (!el || el.nodeType !== 1) return null;
    const cached = _computedStyleCache.get(el);
    if (cached) return cached;
    try {
      const cs = window.getComputedStyle(el);
      const fontFamily = cs.fontFamily;
      const fontSize = cs.fontSize;
      const fontWeight = cs.fontWeight;
      let lineHeight = parseFloat(cs.lineHeight);
      if (!lineHeight || isNaN(lineHeight)) {
        const fs = parseFloat(fontSize) || 16;
        lineHeight = fs * 1.2;
      }
      const obj = { fontFamily, fontSize, fontWeight, lineHeight, csLineHeight: cs.lineHeight };
      _computedStyleCache.set(el, obj);
      return obj;
    } catch (e) {
      trError('getCachedComputedStyle', e);
      return null;
    }
  }

  // Misura il numero di linee di un blocco dato HTML e stile
  function measureLines(elWidth, fontFamily, fontSize, fontWeight, lineHeightStyle, html, lineHeightOverride) {
    const measure = ensureMeasureEl();
    const width = elWidth || Math.min(window.innerWidth, 800);
    try {
      measure.style.width = width + 'px';
      if (fontFamily) measure.style.fontFamily = fontFamily;
      if (fontSize) measure.style.fontSize = fontSize;
      if (fontWeight) measure.style.fontWeight = fontWeight;
      if (lineHeightStyle) measure.style.lineHeight = lineHeightStyle;
      else if (fontSize) measure.style.lineHeight = (parseFloat(fontSize) * 1.2) + 'px';
      else measure.style.lineHeight = '19.2px';
      measure.innerHTML = sanitizeForMeasureWithBudget(html);
      const computedStyle = getComputedStyle(measure);
      const computedLineHeight = lineHeightOverride || parseFloat(computedStyle.lineHeight) || parseFloat(measure.style.lineHeight) || 16;
      const totalHeight = measure.scrollHeight || measure.offsetHeight;
      const lines = Math.max(0, Math.round(totalHeight / computedLineHeight));
      return lines;
    } catch (e) {
      trError('measureLines error', e);
      return 0;
    }
  }

  // Generatore di ID univoci per collegare pulsante e wrapper
  const uid = (prefix = 'tr') => `${prefix}-${Math.random().toString(36).slice(2, 9)}`;

  // Template spacer (riduce allocazioni)
  const _spacerTemplate = (() => {
    const d = document.createElement('div');
    d.className = STYLE_ID + '-spacer';
    d.style.height = SPACER_HEIGHT;
    d.style.minHeight = SPACER_HEIGHT;
    d.setAttribute('aria-hidden', 'true');
    return d;
  })();

  // Clona uno spacer
  const createSpacer = () => _spacerTemplate.cloneNode(true);

  // Garantisce che gli spacer abbiano altezza esplicita
  function ensureSpacersHaveHeight(root) {
    const spacers = root.querySelectorAll('.' + STYLE_ID + '-spacer');
    for (let i = 0; i < spacers.length; i++) {
      const s = spacers[i];
      if (!s.style.height) s.style.height = SPACER_HEIGHT;
      if (!s.style.minHeight) s.style.minHeight = SPACER_HEIGHT;
    }
  }

  // Imposta contenuto HTML sanificato o testo puro
  function setSafeContent(el, html) {
    if (!el) return;
    if (!html) { el.textContent = ''; return; }
    const hasTags = /<[^>]+>/g.test(html);
    if (hasTags) {
      const safe = sanitizeForDisplayWithBudget(html);
      try { el.innerHTML = safe; } catch (e) { trError('setSafeContent innerHTML failed', e); el.textContent = safe.replace(/<[^>]+>/g, ''); }
    } else {
      el.textContent = html;
    }
  }

  // Esegue al prossimo idle o fallback a setTimeout
  function runWhenIdle(cb, options) {
    const timeout = (options && options.timeout) || 2000;
    if (TRUNCATE_CONFIG.useRequestIdleCallback && 'requestIdleCallback' in window) {
      try { requestIdleCallback(cb, { timeout }); return; } catch (e) {}
    }
    setTimeout(cb, 50);
  }

  // Gestione click sul pulsante "Visualizza tutto / Mostra meno"
  function delegatedClickHandler(e) {
    const btn = (e.target && e.target.closest) ? e.target.closest('.vVisualizzaTutto') : null;
    if (!btn) return;
    e.preventDefault();
    const wrapperId = btn.getAttribute('data-target-id');
    if (!wrapperId) return;
    const fragWrapper = document.getElementById(wrapperId);
    if (!fragWrapper) return;
    const maxHeightAttr = btn.getAttribute('data-max-height');
    const maxHeight = maxHeightAttr ? Number(maxHeightAttr) : null;
    const expanded = btn.getAttribute('data-expanded') === 'true';
    const locale = _cachedLocale || detectLocale();
    try {
      if (!expanded) {
        if (OPEN_DURATION_MS > 0) fragWrapper.style.transition = 'max-height ' + OPEN_DURATION_MS + 'ms ease';
        const targetHeight = fragWrapper.scrollHeight;
        fragWrapper.style.maxHeight = targetHeight + 'px';
        btn.textContent = t('hide', locale);
        btn.setAttribute('aria-expanded', 'true');
        btn.setAttribute('data-expanded', 'true');
        if (OPEN_DURATION_MS > 0) {
          setTimeout(() => {
            if (btn.getAttribute('data-expanded') === 'true') fragWrapper.style.maxHeight = 'none';
          }, OPEN_DURATION_MS + 20);
        } else {
          requestAnimationFrame(() => { fragWrapper.style.maxHeight = 'none'; });
        }
      } else {
        if (CLOSE_DURATION_MS === 0) {
          const prev = fragWrapper.style.transition;
          fragWrapper.style.transition = 'none';
          if (maxHeight !== null) fragWrapper.style.maxHeight = maxHeight + 'px';
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              fragWrapper.style.transition = prev || (OPEN_DURATION_MS > 0 ? 'max-height ' + OPEN_DURATION_MS + 'ms ease' : 'none');
            });
          });
        } else {
          fragWrapper.style.transition = 'max-height ' + CLOSE_DURATION_MS + 'ms ease';
          if (fragWrapper.style.maxHeight === 'none' || !fragWrapper.style.maxHeight) {
            const current = fragWrapper.scrollHeight;
            fragWrapper.style.maxHeight = current + 'px';
            requestAnimationFrame(() => {
              if (maxHeight !== null) fragWrapper.style.maxHeight = maxHeight + 'px';
            });
          } else {
            if (maxHeight !== null) fragWrapper.style.maxHeight = maxHeight + 'px';
          }
        }
        btn.textContent = t('show', locale);
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('data-expanded', 'false');
      }
    } catch (err) {
      trError('delegatedClickHandler', err);
    }
  }

  // Accessibilità: attiva click su Space/Enter quando focus è sul pulsante
  function delegatedKeyHandler(e) {
    try {
      const active = document.activeElement;
      if (!active || !active.classList) return;
      if (!active.classList.contains('vVisualizzaTutto')) return;
      const key = e.key || e.keyCode;
      if (key === ' ' || key === 'Spacebar' || key === 32 || key === 'Enter' || key === 13) {
        e.preventDefault();
        active.click();
      }
    } catch (e) {
      trError('delegatedKeyHandler', e);
    }
  }

  // Crea frammento con testo tronco e pulsante, rispettando righe rimanenti
  function createTruncatedNormalFragment(
    elWidth, fontFamily, fontSize, fontWeight, lineHeightStyle,
    normalHtml, lineHeight, remainingLines, locale,
    addTopSpacer, addBottomSpacer
  ) {
    const frag = document.createDocumentFragment();
    if (addTopSpacer) frag.appendChild(createSpacer());
    const fragWrapper = document.createElement('div');
    fragWrapper.className = STYLE_ID + '-text';
    fragWrapper.style.transition = 'none';
    fragWrapper.style.margin = '0';
    fragWrapper.style.padding = '0';
    fragWrapper.style.display = 'block';
    const maxHeight = Math.round(lineHeight * remainingLines);
    fragWrapper.style.maxHeight = maxHeight + 'px';
    setSafeContent(fragWrapper, sanitizeForDisplayWithBudget(normalHtml));
    const wrapperId = uid('truncate');
    fragWrapper.id = wrapperId;
    if (OPEN_DURATION_MS > 0) {
      requestAnimationFrame(() => { fragWrapper.style.transition = 'max-height ' + OPEN_DURATION_MS + 'ms ease'; });
    }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'vVisualizzaTutto';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', wrapperId);
    btn.setAttribute('data-target-id', wrapperId);
    btn.setAttribute('data-expanded', 'false');
    btn.setAttribute('data-max-height', String(maxHeight));
    btn.textContent = t('show', locale);
    btn.style.margin = '0';
    btn.style.padding = '0';
    btn.style.display = 'inline-block';
    btn.style.lineHeight = '1';
    btn.style.verticalAlign = 'middle';
    frag.appendChild(fragWrapper);
    frag.appendChild(btn);
    if (addBottomSpacer) frag.appendChild(createSpacer());
    return frag;
  }

  // Batch di aggiornamenti DOM in rAF, con flag processed e gestione spacer/observer
  const _pendingUpdates = new Map();
  function scheduleDOMUpdate(targetEl, fragment) {
    if (!targetEl) return;
    try {
      if (targetEl.dataset) targetEl.dataset.truncateProcessed = 'true';
    } catch (e) { trError('scheduleDOMUpdate set dataset immediate', e); }

    const existing = _pendingUpdates.get(targetEl);
    if (existing) { existing.appendChild(fragment); return; }

    const container = document.createDocumentFragment();
    container.appendChild(fragment);
    _pendingUpdates.set(targetEl, container);

    requestAnimationFrame(() => {
      const fragToApply = _pendingUpdates.get(targetEl);
      if (!fragToApply) return;
      try {
        try { if (targetEl.dataset) targetEl.dataset.truncateProcessed = 'true'; } catch (e) { trError('scheduleDOMUpdate set dataset', e); }
        if (typeof targetEl.replaceChildren === 'function') {
          targetEl.replaceChildren();
          targetEl.appendChild(fragToApply);
        } else {
          while (targetEl.firstChild) targetEl.removeChild(targetEl.firstChild);
          targetEl.appendChild(fragToApply);
        }
        ensureSpacersHaveHeight(targetEl);
        targetEl.style.visibility = 'visible';
        try { observeNodeForResize(targetEl); } catch (e) { trError('observe after scheduleDOMUpdate', e); }
      } catch (e) {
        trError('scheduleDOMUpdate apply', e);
      }
      _pendingUpdates.delete(targetEl);
    });
  }

  // Cache locale per sanitizzazione display durante processElement
  function sanitizeForDisplayWithCache(html, cacheMap) {
    if (!html) return '';
    const c = cacheMap.get(html);
    if (c !== undefined) return c;
    const s = sanitizeForDisplayWithBudget(html);
    cacheMap.set(html, s);
    return s;
  }

  // Core: processa un elemento target, misura righe e tronca se supera soglia
  function processElement(el, locale) {
    if (!el || el.dataset.truncateProcessed === 'true') {
      if (el) el.style.visibility = 'visible';
      return;
    }
    try {
      const children = Array.from(el.childNodes);
      const csCached = getCachedComputedStyle(el) || {};
      const fontFamily = csCached.fontFamily;
      const fontSize = csCached.fontSize;
      const fontWeight = csCached.fontWeight;
      const lineHeight = csCached.lineHeight;
      const csLineHeight = csCached.csLineHeight;

      const elRect = el.getBoundingClientRect();
      const elWidth = (elRect && elRect.width) ? elRect.width : el.clientWidth || Math.min(window.innerWidth, 800);

      const linesAttr = el.getAttribute('data-truncate-lines');
      const threshold = (linesAttr && !isNaN(parseInt(linesAttr, 10))) ? parseInt(linesAttr, 10) : LINES_DEFAULT;

      const localSanitizeDisplayCache = new Map();
      let normalAccumulator = document.createElement('div');
      let cumulativeNormalLines = 0;
      let reachedThreshold = false;
      const outputFragment = document.createDocumentFragment();
      let previousWasDiv = false;

      for (let i = 0; i < children.length; i++) {
        const node = children[i];

        // Quando incontriamo un DIV, chiudiamo l'accumulatore "normale" e decidiamo se troncare o no
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'DIV') {
          if (normalAccumulator.childNodes.length > 0) {
            const normalHtml = normalAccumulator.innerHTML;
            const normalLines = measureLines(elWidth, fontFamily, fontSize, fontWeight, csLineHeight, normalHtml, lineHeight);
            if (!reachedThreshold) {
              if (cumulativeNormalLines + normalLines <= threshold) {
                if (previousWasDiv) outputFragment.appendChild(createSpacer());
                const normalContainer = document.createElement('div');
                normalContainer.style.margin = '0';
                normalContainer.style.padding = '0';
                setSafeContent(normalContainer, sanitizeForDisplayWithCache(normalHtml, localSanitizeDisplayCache));
                outputFragment.appendChild(normalContainer);
                cumulativeNormalLines += normalLines;
                previousWasDiv = false;
              } else {
                const remaining = Math.max(0, threshold - cumulativeNormalLines);
                if (remaining > 0) {
                  const truncated = createTruncatedNormalFragment(
                    elWidth, fontFamily, fontSize, fontWeight, csLineHeight,
                    normalHtml, lineHeight, remaining, locale, previousWasDiv, true
                  );
                  outputFragment.appendChild(truncated);
                  previousWasDiv = false;
                }
                reachedThreshold = true;
              }
            }
            normalAccumulator = document.createElement('div');
          }

          // Cloniamo e sanifichiamo il DIV corrente
          const divClone = node.cloneNode(true);
          if (divClone && divClone.innerHTML) {
            const sanitized = sanitizeForDisplayWithCache(divClone.innerHTML, localSanitizeDisplayCache);
            setSafeContent(divClone, sanitized);
          }
          if (divClone.style) {
            divClone.style.margin = divClone.style.margin || '0';
            divClone.style.padding = divClone.style.padding || '0';
          }
          if (previousWasDiv) outputFragment.appendChild(createSpacer());
          outputFragment.appendChild(divClone);
          previousWasDiv = true;
          continue;
        }

        // Accumula nodi non-DIV per misura aggregata
        normalAccumulator.appendChild(node.cloneNode(true));
      }

      // Chiusura finale dell'accumulatore normale
      if (normalAccumulator.childNodes.length > 0 && !reachedThreshold) {
        const normalHtml = normalAccumulator.innerHTML;
        const normalLines = measureLines(elWidth, fontFamily, fontSize, fontWeight, csLineHeight, normalHtml, lineHeight);
        if (cumulativeNormalLines + normalLines <= threshold) {
          if (previousWasDiv) outputFragment.appendChild(createSpacer());
          const normalContainer = document.createElement('div');
          normalContainer.style.margin = '0';
          normalContainer.style.padding = '0';
          setSafeContent(normalContainer, sanitizeForDisplayWithCache(normalHtml, localSanitizeDisplayCache));
          outputFragment.appendChild(normalContainer);
          cumulativeNormalLines += normalLines;
          previousWasDiv = false;
        } else {
          const remaining = Math.max(0, threshold - cumulativeNormalLines);
          if (remaining > 0) {
            const truncated = createTruncatedNormalFragment(
              elWidth, fontFamily, fontSize, fontWeight, csLineHeight,
              normalHtml, lineHeight, remaining, locale, previousWasDiv, false
            );
            outputFragment.appendChild(truncated);
            previousWasDiv = false;
          }
          reachedThreshold = true;
        }
      }

      // Se non abbiamo superato soglia, rendiamo visibile e attacchiamo observer; altrimenti applichiamo frammento
      if (!reachedThreshold) {
        try { if (el.dataset) el.dataset.truncateProcessed = 'true'; } catch (e) { trError('set processed flag', e); }
        el.style.visibility = 'visible';
        try { observeNodeForResize(el); } catch (e) { trError('observe after processElement', e); }
        return;
      }
      scheduleDOMUpdate(el, outputFragment);
    } catch (e) {
      trError('processElement', e);
      try { el.style.visibility = 'visible'; } catch (err) { trError('processElement visibility fail', err); }
    }
  }

  // Cache nodi target nel documento per ridurre query ripetute
  let _cachedNodes = null;
  function getCachedNodes(root) {
    if (root === document && _cachedNodes) return _cachedNodes;
    const nodes = (root && root.querySelectorAll) ? Array.from(root.querySelectorAll(SEL)) : [];
    if (root === document) _cachedNodes = nodes;
    return nodes;
  }

  // Invalidazione selettiva delle cache (sanitizzazione opzionale)
  function invalidateCachesSelective(options) {
    options = options || {};
    const clearSanitize = !!options.clearSanitize;
    _cachedNodes = null;
    _computedStyleCache = new WeakMap();
    _cachedLocale = null;
    if (clearSanitize) {
      _sanitizeMeasureCache.clear();
      _sanitizeDisplayCache.clear();
    }
  }

  // Processa tutti i nodi sotto root: gestisce document fragment e match diretto
  function processAll(root) {
    root = root || document;
    _cachedLocale = detectLocale();
    const locale = _cachedLocale;
    if (!root || !root.querySelectorAll) return;

    if (root.nodeType === 11) {
      const fragChildren = Array.from(root.childNodes || []);
      for (let i = 0; i < fragChildren.length; i++) {
        const child = fragChildren[i];
        if (child && child.nodeType === 1) processAll(child);
      }
      return;
    }

    if (root.nodeType === 1 && root.matches && root.matches(SEL)) processElement(root, locale);
    const nodes = getCachedNodes(root);
    for (let i = 0; i < nodes.length; i++) processElement(nodes[i], locale);
  }

  // Funzione esposta: processa tutte le descrizioni (entry-point pubblico)
  window.truncateDescriptionProcessAll = root => processAll(root || document);

  // Funzione esposta: forza risanificazione dei nodi e riprocessa
  window.__TRUNCATE_forceResanitize = function (root) {
    try {
      runWhenIdle(() => invalidateCachesSelective({ clearSanitize: true }), { timeout: 1000 });
      let nodes = [];
      if (root === document) {
        nodes = Array.from(document.querySelectorAll(SEL));
      } else if (root && root.querySelectorAll) {
        nodes = Array.from(root.querySelectorAll(SEL));
        if (root.nodeType === 1 && root.matches && root.matches(SEL)) nodes.push(root);
      } else if (root && root.nodeType === 1 && root.matches && root.matches(SEL)) {
        nodes = [root];
      }

      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        try {
          const before = n.innerHTML;
          const after = sanitizeForDisplayWithBudget(before);
          if (after !== before) {
            try { n.innerHTML = after; } catch (e) { trError('forceResanitize innerHTML fail', e); n.textContent = after.replace(/<[^>]+>/g, ''); }
          }
          if (n.dataset) n.dataset.truncateProcessed = 'true';
        } catch (e) { trError('forceResanitize node', e); }
      }

      processAll(root || document);
      return true;
    } catch (e) {
      trError('__TRUNCATE_forceResanitize', e);
      return false;
    }
  };

  // Reprocess globale (debounce) su eventi di layout
  let _scheduledReprocess = false;
  let _lastScheduledAt = 0;
  function scheduleGlobalReprocess() {
    const now = Date.now();
    if (_scheduledReprocess) return;
    if (now - _lastScheduledAt < 200) return;
    _lastScheduledAt = now;
    _scheduledReprocess = true;
    _cachedNodes = null;
    runWhenIdle(() => {
      processAll(document);
      invalidateCachesSelective({ clearSanitize: false });
      _scheduledReprocess = false;
    }, { timeout: 300 });
  }

  // ResizeObserver per riprocessare i nodi visibili quando cambiano dimensioni
  let _ro = null;
  const _observed = new Set();
  function isElementVisibleForObservation(el) {
    if (!el || el.nodeType !== 1) return false;
    if (!el.isConnected) return false;
    if (typeof el.offsetParent !== 'undefined' && el.offsetParent === null) {
      return el.getClientRects && el.getClientRects().length > 0;
    }
    return true;
  }
  function observeNodeForResize(node) {
    if (!_ro || typeof _ro.observe !== 'function') return;
    if (!node || node.nodeType !== 1) return;
    try {
      if (!_observed.has(node) && isElementVisibleForObservation(node)) {
        _ro.observe(node);
        _observed.add(node);
      }
    } catch (e) { trError('observeNodeForResize', e); }
  }
  function attachResizeObserver() {
    if (_ro) return;
    if (typeof ResizeObserver !== 'undefined') {
      _ro = new ResizeObserver(entries => {
        const toProcess = [];
        for (let i = 0; i < entries.length; i++) {
          const target = entries[i].target;
          if (target && target.nodeType === 1 && target.matches && target.matches(SEL)) {
            if (!isElementVisibleForObservation(target)) continue;
            toProcess.push(target);
          }
        }
        if (toProcess.length) {
          for (let i = 0; i < toProcess.length; i++) processElement(toProcess[i], _cachedLocale || detectLocale());
        }
      });
    }
  }

  // Listener globali: click/keydown delegati, resize/orientationchange, MutationObserver
  function attachGlobalListeners() {
    try {
      document.addEventListener('click', delegatedClickHandler, true);
      document.addEventListener('keydown', delegatedKeyHandler, true);
      window.addEventListener('resize', scheduleGlobalReprocess, true);
      window.addEventListener('orientationchange', scheduleGlobalReprocess, true);
      const mo = (typeof MutationObserver !== 'undefined') ? new MutationObserver(mutations => {
        try {
          let sawAdded = false;
          for (let i = 0; i < mutations.length; i++) {
            const m = mutations[i];
            if (m.type === 'childList' && m.addedNodes && m.addedNodes.length) {
              for (let j = 0; j < m.addedNodes.length; j++) {
                const node = m.addedNodes[j];
                if (!node || node.nodeType !== 1) continue;
                try {
                  if ((node.matches && node.matches(SEL)) || (node.querySelector && node.querySelector(SEL))) {
                    try { if (node.dataset) node.dataset.truncateProcessed = 'true'; } catch (e) { trError('mo set dataset', e); }
                    requestAnimationFrame(() => {
                      try {
                        if (node.matches && node.matches(SEL)) processElement(node, _cachedLocale || detectLocale());
                        const descendants = node.querySelectorAll ? Array.from(node.querySelectorAll(SEL)) : [];
                        for (let k = 0; k < descendants.length; k++) processElement(descendants[k], _cachedLocale || detectLocale());
                      } catch (e) { trError('mo processElement', e); }
                    });
                    sawAdded = true;
                  }
                } catch (e) { trError('mo check node', e); }
              }
              if (sawAdded) {
                invalidateCachesSelective({ clearSanitize: false });
                scheduleGlobalReprocess();
                break;
              }
            }
          }
        } catch (e) { trError('mutation observer outer', e); }
      }) : null;
      if (mo) mo.observe(document.documentElement || document.body, { childList: true, subtree: true });
    } catch (e) {
      trError('attachGlobalListeners', e);
    }
  }

  // Bootstrap: attacca observer/listener e processa inizialmente i nodi
  runWhenIdle(() => {
    try {
      attachResizeObserver();
      attachGlobalListeners();
      processAll(document);
    } catch (e) {
      trError('init', e);
    }
  }, { timeout: 1000 });
})();
